import React, { useEffect, useRef, useState } from "react";
import { FiPlay, FiPause } from "react-icons/fi";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import heroVideo from "./assets/fight.mp4";
import heroPoster from "./assets/hero1.jpeg";
import "./HeroSection.css";

const HeroSection = () => {
  const videoRef = useRef(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const userPausedRef = useRef(false);

  const playVideo = async () => {
    const video = videoRef.current;
    if (!video || userPausedRef.current) return;
    try {
      await video.play();
    } catch {
      // Browsers may block autoplay; the play control remains available.
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = true;
    video.playsInline = true;
    video.loop = true;

    const tryPlayVideo = () => {
      if (!userPausedRef.current) video.play()?.catch(() => {});
    };
    tryPlayVideo();

    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        video.pause();
      } else if (video.paused) {
        tryPlayVideo();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  const toggleVideo = async () => {
    if (!videoRef.current) return;
    if (!videoRef.current.paused) {
      userPausedRef.current = true;
      videoRef.current.pause();
    } else {
      userPausedRef.current = false;
      await playVideo();
    }
  };

  return (
    <section className="home-hero relative w-full overflow-hidden bg-black">
      <div className="home-hero__media">
      <video
        ref={videoRef}
        className="home-hero__video"
        autoPlay
        muted
        playsInline
        preload="auto"
        loop
        poster={heroPoster}
        onCanPlay={playVideo}
        onPlay={() => setIsVideoPlaying(true)}
        onPause={() => setIsVideoPlaying(false)}
        onError={() => setIsVideoPlaying(false)}
      >
        <source src={heroVideo} type="video/mp4" />
      </video>

      <div className="home-hero__controls">
        <button
          onClick={toggleVideo}
          type="button"
          aria-label={isVideoPlaying ? "Pause video" : "Play video"}
          aria-pressed={isVideoPlaying}
          className="home-hero__control bg-black/40 backdrop-blur-sm rounded-full text-white border border-white/20 hover:bg-black/60 transition"
        >
          {isVideoPlaying ? <FiPause size={20} /> : <FiPlay size={20} />}
        </button>
      </div>
      </div>

      {/* Keep store copy clear of the lettering embedded in the video. */}
      <div className="home-hero__content">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="home-hero__copy"
        >
          <h1 className="home-hero__title font-extrabold text-white leading-tight">
            FIGHT LIKE
            {" "}<span className="text-red-500">A CHAMPION</span>
          </h1>

          <p className="home-hero__description text-white">
            Premium martial arts gear built for real fighters who train without limits.
          </p>

        </motion.div>
        <Link
            to="/products"
            className="home-hero__cta bg-red-600 hover:bg-red-700 text-white font-bold rounded-full shadow-lg hover:scale-105 transition"
          >
            JOIN THE FIGHT
        </Link>
      </div>
    </section>
  );
};

export default HeroSection;
